import { RNG } from "rot-js"
import { Color } from "../defines/colors"
import { AttackType, EnemyType, ItemType } from "../defines/enums"
import { Symbol } from "../defines/symbols"
import { EnemyConfig, EnemySpec, Position } from "../defines/types"
import { EnemyDefaults } from "../defines/values"
import { BounceAsyncEnemy } from "../entity/enemies/bounce/async"
import { BounceSyncEnemy } from "../entity/enemies/bounce/sync"
import { Enemy } from "../entity/enemies/enemy"
import { Trap } from "../entity/enemies/other/trap"
import { PathAsyncEnemy } from "../entity/enemies/path/async"
import { PathSyncEnemy } from "../entity/enemies/path/sync"
import { Player } from "../player/player"
import { generateRandomNumber } from "./rng"

function getOptic(optic?: ItemType): ItemType | undefined {
  const optics = [
    ItemType.OPTIC_RED,
    ItemType.OPTIC_GREEN,
    ItemType.OPTIC_BLUE,
    ItemType.OPTIC_MAGENTA,
    ItemType.OPTIC_CYAN,
    ItemType.OPTIC_YELLOW,
  ]

  if (optic === undefined) {
    return undefined
  } else if (optic == ItemType.OPTIC_RANDOM) {
    return optics[generateRandomNumber(0, optics.length - 1)]
  }

  return optic
}

// RIFT    -- static
// VOID    -- sync bounce
// BLADE   -- async bounce
// SPECTRE -- sync  path & chase
// REAVER  -- async path & chase

const EnemyConfigs = {
  RIFT_ESSENCE: {
    name: "Essence Rift",
    experience: 10,
    combat: {
      essence: 6,
      maxEssence: 6,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 2,
      attackType: AttackType.DRAIN_ESSENCE,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.RIFT_1, Symbol.RIFT_2],
      light: Color.RIFT_ESSENCE,
      dark: Color.RIFT_ESSENCE_DARK,
    },
  },
  RIFT_PIGMENT: {
    name: "Color Rift",
    experience: 10,
    combat: {
      essence: 6,
      maxEssence: 6,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 1,
      attackType: AttackType.DRAIN_PIGMENT,
      attackMessage: "pigment drained",
    },
    render: {
      symbol: [Symbol.RIFT_1, Symbol.RIFT_2],
      light: Color.RIFT_PIGMENT,
      dark: Color.RIFT_PIGMENT_DARK,
    },
  },
  RIFT_BOTH: {
    name: "Corrupt Rift",
    experience: 12,
    combat: {
      essence: 7,
      maxEssence: 7,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 2,
      attackType: AttackType.DRAIN_ESSENCE_AND_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.RIFT_1, Symbol.RIFT_2],
      light: Color.RIFT_BOTH,
      dark: Color.RIFT_BOTH_DARK,
    },
  },
  SPECTRE_ESSENCE: {
    name: "Essence Spectre",
    experience: 65,
    combat: {
      essence: 29,
      maxEssence: 29,
      essenceDefense: 0,
      minAttack: 8,
      maxAttack: 12,
      attackType: AttackType.DRAIN_ESSENCE,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.SPECTRE_1, Symbol.SPECTRE_2],
      light: Color.SPECTRE_ESSENCE,
      dark: Color.SPECTRE_ESSENCE_DARK,
    },
  },
  SPECTRE_PIGMENT: {
    name: "Color Spectre",
    experience: 65,
    combat: {
      essence: 29,
      maxEssence: 29,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 1,
      attackType: AttackType.DRAIN_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.SPECTRE_1, Symbol.SPECTRE_2],
      light: Color.SPECTRE_PIGMENT,
      dark: Color.SPECTRE_PIGMENT_DARK,
    },
    optic: ItemType.OPTIC_RANDOM,
  },
  SPECTRE_BOTH: {
    name: "Elite Spectre",
    experience: 72,
    combat: {
      essence: 32,
      maxEssence: 32,
      essenceDefense: 0,
      minAttack: 8,
      maxAttack: 12,
      attackType: AttackType.DRAIN_ESSENCE_AND_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.SPECTRE_1, Symbol.SPECTRE_2],
      light: Color.SPECTRE_BOTH,
      dark: Color.SPECTRE_BOTH_DARK,
    },
    optic: ItemType.OPTIC_RANDOM,
  },
  REAVER_ESSENCE: {
    name: "Essence Reaver",
    experience: 90,
    combat: {
      essence: 36,
      maxEssence: 36,
      essenceDefense: 0,
      minAttack: 10,
      maxAttack: 15,
      attackType: AttackType.DRAIN_ESSENCE,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.REAVER_1, Symbol.REAVER_2],
      light: Color.REAVER_ESSENCE,
      dark: Color.REAVER_ESSENCE_DARK,
    },
  },
  REAVER_PIGMENT: {
    name: "Color Reaver",
    experience: 90,
    combat: {
      essence: 36,
      maxEssence: 36,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 1,
      attackType: AttackType.DRAIN_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.REAVER_1, Symbol.REAVER_2],
      light: Color.REAVER_PIGMENT,
      dark: Color.REAVER_PIGMENT_DARK,
    },
  },
  REAVER_BOTH: {
    name: "Elite Reaver",
    experience: 100,
    combat: {
      essence: 45,
      maxEssence: 45,
      essenceDefense: 0,
      minAttack: 10,
      maxAttack: 18,
      attackType: AttackType.DRAIN_ESSENCE_AND_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.REAVER_1, Symbol.REAVER_2],
      light: Color.REAVER_BOTH,
      dark: Color.REAVER_BOTH_DARK,
    },
  },
  BLADE_ESSENCE: {
    name: "Essence Blade",
    experience: 29,
    combat: {
      essence: 18,
      maxEssence: 18,
      essenceDefense: 0,
      minAttack: 5,
      maxAttack: 8,
      attackType: AttackType.DRAIN_ESSENCE,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.BLADE_1, Symbol.BLADE_2],
      light: Color.BLADE_ESSENCE,
      dark: Color.BLADE_ESSENCE_DARK,
    },
  },
  BLADE_PIGMENT: {
    name: "Color Blade",
    experience: 29,
    combat: {
      essence: 18,
      maxEssence: 18,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 1,
      attackType: AttackType.DRAIN_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.BLADE_1, Symbol.BLADE_2],
      light: Color.BLADE_PIGMENT,
      dark: Color.BLADE_PIGMENT_DARK,
    },
  },
  BLADE_BOTH: {
    name: "Corrupt Blade",
    experience: 32,
    combat: {
      essence: 20,
      maxEssence: 20,
      essenceDefense: 0,
      minAttack: 5,
      maxAttack: 8,
      attackType: AttackType.DRAIN_ESSENCE_AND_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.BLADE_1, Symbol.BLADE_2],
      light: Color.BLADE_BOTH,
      dark: Color.BLADE_BOTH_DARK,
    },
  },
  VOID_ESSENCE: {
    name: "Essence Void",
    experience: 23,
    combat: {
      essence: 8,
      maxEssence: 8,
      essenceDefense: 0,
      minAttack: 3,
      maxAttack: 5,
      attackType: AttackType.DRAIN_ESSENCE,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.VOID_HORZ_1, Symbol.VOID_HORZ_2],
      light: Color.VOID_ESSENCE,
      dark: Color.VOID_ESSENCE_DARK,
    },
  },
  VOID_PIGMENT: {
    name: "Color Void",
    experience: 23,
    combat: {
      essence: 8,
      maxEssence: 8,
      essenceDefense: 0,
      minAttack: 1,
      maxAttack: 1,
      attackType: AttackType.DRAIN_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.VOID_HORZ_1, Symbol.VOID_HORZ_2],
      light: Color.VOID_PIGMENT,
      dark: Color.VOID_PIGMENT_DARK,
    },
  },
  VOID_BOTH: {
    name: "Corrupt Void",
    experience: 26,
    combat: {
      essence: 9,
      maxEssence: 9,
      essenceDefense: 0,
      minAttack: 3,
      maxAttack: 5,
      attackType: AttackType.DRAIN_ESSENCE_AND_PIGMENT,
      attackMessage: "essence drained",
    },
    render: {
      symbol: [Symbol.VOID_HORZ_1, Symbol.VOID_HORZ_2],
      light: Color.VOID_BOTH,
      dark: Color.VOID_BOTH_DARK,
    },
  },
}

function getEnemyConfig(type: EnemyType): EnemyConfig | undefined {
  switch (type) {
    case EnemyType.RIFT_ESSENCE:
      return EnemyConfigs.RIFT_ESSENCE
    case EnemyType.RIFT_PIGMENT:
      return EnemyConfigs.RIFT_PIGMENT
    case EnemyType.RIFT_BOTH:
      return EnemyConfigs.RIFT_BOTH
    case EnemyType.SPECTRE_ESSENCE:
      return EnemyConfigs.SPECTRE_ESSENCE
    case EnemyType.SPECTRE_PIGMENT:
      return EnemyConfigs.SPECTRE_PIGMENT
    case EnemyType.SPECTRE_BOTH:
      return EnemyConfigs.SPECTRE_BOTH
    case EnemyType.REAVER_ESSENCE:
      return EnemyConfigs.REAVER_ESSENCE
    case EnemyType.REAVER_PIGMENT:
      return EnemyConfigs.REAVER_PIGMENT
    case EnemyType.REAVER_BOTH:
      return EnemyConfigs.REAVER_BOTH
    case EnemyType.BLADE_ESSENCE:
      return EnemyConfigs.BLADE_ESSENCE
    case EnemyType.BLADE_PIGMENT:
      return EnemyConfigs.BLADE_PIGMENT
    case EnemyType.BLADE_BOTH:
      return EnemyConfigs.BLADE_BOTH
    case EnemyType.VOID_ESSENCE:
      return EnemyConfigs.VOID_ESSENCE
    case EnemyType.VOID_PIGMENT:
      return EnemyConfigs.VOID_PIGMENT
    case EnemyType.VOID_BOTH:
      return EnemyConfigs.VOID_BOTH
    default:
  }

  return undefined
}

function isAsyncEnemy(type: EnemyType): boolean {
  switch (type) {
    case EnemyType.REAVER_ESSENCE:
    case EnemyType.REAVER_PIGMENT:
    case EnemyType.REAVER_BOTH:
    case EnemyType.BLADE_ESSENCE:
    case EnemyType.BLADE_PIGMENT:
    case EnemyType.BLADE_BOTH:
      return true
  }

  return false
}

function isEnemy(type: EnemyType): boolean {
  switch (type) {
    case EnemyType.RIFT_ESSENCE:
    case EnemyType.RIFT_PIGMENT:
    case EnemyType.RIFT_BOTH:
    case EnemyType.SPECTRE_ESSENCE:
    case EnemyType.SPECTRE_PIGMENT:
    case EnemyType.SPECTRE_BOTH:
    case EnemyType.REAVER_ESSENCE:
    case EnemyType.REAVER_PIGMENT:
    case EnemyType.REAVER_BOTH:
    case EnemyType.BLADE_ESSENCE:
    case EnemyType.BLADE_PIGMENT:
    case EnemyType.BLADE_BOTH:
    case EnemyType.VOID_ESSENCE:
    case EnemyType.VOID_PIGMENT:
    case EnemyType.VOID_BOTH:
      return true
  }

  return false
}

function makeEnemy(
  position: Position,
  spec: EnemySpec,
  player: Player
): Enemy | undefined {
  const config = getEnemyConfig(spec.type)

  if (config === undefined) {
    return undefined
  }

  let enemy = undefined

  const fov = generateRandomNumber(
    EnemyDefaults.MIN_FOV_RANGE,
    EnemyDefaults.MAX_FOV_RANGE
  )
  const optic = undefined // getOptic(config.optic)

  switch (spec.type) {
    case EnemyType.RIFT_ESSENCE:
    case EnemyType.RIFT_PIGMENT:
    case EnemyType.RIFT_BOTH:
      enemy = new Enemy(
        spec.type,
        config.experience,
        config.combat,
        position,
        config.render,
        config.name,
        optic
      )
      break
    case EnemyType.SPECTRE_ESSENCE:
    case EnemyType.SPECTRE_PIGMENT:
    case EnemyType.SPECTRE_BOTH:
      enemy = new PathSyncEnemy(
        spec.type,
        config.experience,
        config.combat,
        position,
        config.render,
        config.name,
        fov,
        optic
      )
      break
    case EnemyType.REAVER_ESSENCE:
    case EnemyType.REAVER_PIGMENT:
    case EnemyType.REAVER_BOTH:
      enemy = new PathAsyncEnemy(
        spec.type,
        config.experience,
        config.combat,
        position,
        config.render,
        config.name,
        fov,
        optic
      )
      break
    case EnemyType.VOID_ESSENCE:
    case EnemyType.VOID_PIGMENT:
    case EnemyType.VOID_BOTH:
      enemy = new BounceSyncEnemy(
        spec.type,
        config.experience,
        config.combat,
        position,
        config.render,
        config.name,
        config.direction,
        optic
      )
      break
    case EnemyType.BLADE_ESSENCE:
    case EnemyType.BLADE_PIGMENT:
    case EnemyType.BLADE_BOTH:
      enemy = new BounceAsyncEnemy(
        spec.type,
        config.experience,
        config.combat,
        position,
        config.render,
        config.name,
        config.direction,
        optic
      )
      break
    default:
      break
  }

  return enemy
}

function makeTrap(
  position: Position,
  colors: [string, number][]
): Trap | undefined {
  let name = RNG.getWeightedValue(Object.fromEntries(colors))
  if (name === undefined) {
    return undefined
  }

  let light = Color.PIGMENT_LIGHT_YELLOW
  let dark = Color.PIGMENT_DARK_YELLOW
  let optic = ItemType.OPTIC_YELLOW

  switch (name) {
    case "red":
      light = Color.PIGMENT_LIGHT_RED
      dark = Color.PIGMENT_DARK_RED
      optic = ItemType.OPTIC_RED
      break
    case "magenta":
      light = Color.PIGMENT_LIGHT_MAGENTA
      dark = Color.PIGMENT_DARK_MAGENTA
      optic = ItemType.OPTIC_MAGENTA
      break
    case "blue":
      light = Color.PIGMENT_LIGHT_BLUE
      dark = Color.PIGMENT_DARK_BLUE
      optic = ItemType.OPTIC_BLUE
      break
    case "cyan":
      light = Color.PIGMENT_LIGHT_CYAN
      dark = Color.PIGMENT_DARK_CYAN
      optic = ItemType.OPTIC_CYAN
      break
    case "green":
      light = Color.PIGMENT_LIGHT_GREEN
      dark = Color.PIGMENT_DARK_GREEN
      optic = ItemType.OPTIC_GREEN
      break
    default:
      break
  }

  const render = { symbol: [Symbol.DEVOURER], light: light, dark: dark }
  return new Trap(position, render, optic)
}

export { getEnemyConfig, isAsyncEnemy, isEnemy, makeEnemy, makeTrap }
